import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

/**
 * GuidanceContext — live, PII-safe "where is the client right now" state that
 * the FAQ assistant folds into each turn so it can answer page/field-specific
 * questions ("what do I put here?") without the client explaining themselves.
 *
 * Producers (currently IntakeFormV2) push form-level detail via `setForm`. The
 * route is read here directly from react-router, so any page has route-level
 * awareness with no wiring at all.
 *
 * Privacy: `snapshot()` deliberately emits only human-readable LABELS, section
 * titles, error messages and outstanding-item labels — never field values, and
 * never the client's visa subclass or case status. This keeps the assistant's
 * prompt free of raw PII (Phase 1 decision). See CONTEXTUAL-GUIDANCE-PHASE-1-
 * BUILD-SPEC.md.
 */

export interface GuidanceField {
  /** react-hook-form field name (internal; never sent to the model). */
  key: string;
  /** Human label the client already sees on screen. */
  label: string;
  /** Current validation message for this field, if any. */
  error?: string;
}

export interface GuidanceOutstanding {
  /** Human section titles that are not yet complete. */
  sectionsIncomplete: string[];
  /** Human document labels still required. */
  documentsMissing: string[];
  /** Overall intake completion, 0-100. */
  percent?: number;
}

export interface GuidanceForm {
  sectionId?: string;
  sectionTitle?: string;
  stage?: 1 | 2;
  activeField?: GuidanceField;
  visibleErrors?: GuidanceField[];
  outstanding?: GuidanceOutstanding;
}

export interface GuidanceValue {
  form: GuidanceForm | null;
  setForm: Dispatch<SetStateAction<GuidanceForm | null>>;
  /** Serialised, PII-safe snapshot for the assistant. Route always included. */
  snapshot: () => Record<string, unknown>;
}

const GuidanceContext = createContext<GuidanceValue | null>(null);

// Map a pathname to a plain-English page name the bot can use in its answer.
function pageLabel(pathname: string): string {
  if (pathname.includes('/intake')) return 'Intake form';
  if (pathname.includes('/documents')) return 'Documents';
  if (pathname.includes('/form-80')) return 'Form 80';
  if (pathname.includes('/deadlines')) return 'Deadlines';
  if (pathname.includes('/account')) return 'Account settings';
  if (pathname.includes('/messages')) return 'Contact';
  if (pathname.includes('/dashboard')) return 'Dashboard';
  return 'the portal';
}

/**
 * Pure serialisation of guidance state into the PII-safe snapshot sent to the
 * assistant. Emits human labels, section titles, error text and outstanding-item
 * labels only — never field values, never the visa subclass. Arrays are bounded.
 * Exported for testing.
 */
export function serialiseGuidance(pathname: string, form: GuidanceForm | null): Record<string, unknown> {
  const s: Record<string, unknown> = { route: pathname, page: pageLabel(pathname) };
  if (form?.sectionTitle) s.section = form.sectionTitle;
  if (form?.stage) s.stage = form.stage;
  if (form?.activeField) {
    s.active_field = { label: form.activeField.label, error: form.activeField.error };
  }
  if (form?.visibleErrors?.length) {
    s.errors = form.visibleErrors.slice(0, 12).map((e) => ({ field: e.label, message: e.error }));
  }
  if (form?.outstanding) {
    const o = form.outstanding;
    const out: Record<string, unknown> = {};
    if (o.sectionsIncomplete.length) out.sections_incomplete = o.sectionsIncomplete.slice(0, 20);
    if (o.documentsMissing.length) out.documents_missing = o.documentsMissing.slice(0, 30);
    if (typeof o.percent === 'number') out.percent = o.percent;
    if (Object.keys(out).length) s.outstanding = out;
  }
  return s;
}

export function GuidanceProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [form, setForm] = useState<GuidanceForm | null>(null);

  const snapshot = useCallback((): Record<string, unknown> => serialiseGuidance(pathname, form), [pathname, form]);

  const value = useMemo<GuidanceValue>(() => ({ form, setForm, snapshot }), [form, snapshot]);
  return <GuidanceContext.Provider value={value}>{children}</GuidanceContext.Provider>;
}

/**
 * Read the guidance context. Falls back to an inert no-op when used outside a
 * provider, so a producer page (e.g. the intake form) can never crash if it is
 * ever rendered without the provider mounted.
 */
export function useGuidance(): GuidanceValue {
  const ctx = useContext(GuidanceContext);
  if (ctx) return ctx;
  return {
    form: null,
    setForm: () => {},
    snapshot: () => ({}),
  };
}
